import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  initialSnap: "Half" | "Full" | number;
  minHeight: number;
  maxHeight: number;
  children: ReactNode;
}
export default function BottomSheet({
  open,
  onClose,
  initialSnap = "Half",
  minHeight = 120,
  maxHeight = 500,
  children,
}: BottomSheetProps) {
  const backdropRef = useRef(null);
  const sheetRef = useRef(null);
  const animRef = useRef<number | null>(null);
  const [viewportH, setViewportH] = useState(
    typeof window !== "undefined" ? window.innerHeight : 800
  );

  const computedMaxHeight = Math.min(
    maxHeight ?? Math.round(viewportH * 0.9),
    viewportH
  );

  // (0 means fully open at maxHeight)
  const [translateY, setTranslateY] = useState(0);

  // Prevent background scroll while open/dragging
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Track viewport changes (for mobile address bar shrink/expand)
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Determine initial snap position when opening
  useEffect(() => {
    if (!open) return;
    let initialOpenHeight;
    if (typeof initialSnap === "number") {
      initialOpenHeight = Math.max(
        minHeight,
        Math.min(initialSnap, computedMaxHeight)
      );
    } else if (initialSnap === "Full") {
      initialOpenHeight = computedMaxHeight;
    } else {
      // Half
      initialOpenHeight = Math.max(
        minHeight,
        Math.round(computedMaxHeight * 0.5)
      );
    }
    // translateY is distance from fully open (maxHeight) downwards
    // If sheet height is h, translateY = maxHeight - h
    const ty = computedMaxHeight - initialOpenHeight;
    // Start slightly below and animate up
    setTranslateY(ty + 40);
    requestAnimationFrame(() => {
      setTranslateY(ty);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, computedMaxHeight, minHeight, initialSnap]);

  const clamp = (val: number, min: number, max: number) =>
    Math.max(min, Math.min(max, val));

  const snapPoints = [
    // Closed
    computedMaxHeight, // translateY position when fully closed (sheet off-screen)
    // Min height (peek)
    clamp(computedMaxHeight - minHeight, 0, computedMaxHeight),
    // Half
    clamp(Math.round(computedMaxHeight * 0.5), 0, computedMaxHeight),
    // Full open
    0,
  ];

  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

  const animateTo = useCallback(
    (targetTY: number) => {
      if (animRef.current !== null) cancelAnimationFrame(animRef.current);

      const start = performance.now();
      const startY = translateY;
      const duration = 220; // ms
      const delta = targetTY - startY;

      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = easeOutCubic(t);
        const y = startY + delta * eased;
        setTranslateY(y);

        if (t < 1) {
          animRef.current = requestAnimationFrame(step);
        } else {
          animRef.current = null;
          if (targetTY >= computedMaxHeight - 1) {
            onClose?.();
          }
        }
      };

      animRef.current = requestAnimationFrame(step);
    },
    [translateY, computedMaxHeight, onClose]
  );

  const onBackdropClick = (e: any) => {
    if (e.target === backdropRef.current) {
      animateTo(computedMaxHeight); // close
    }
  };

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: any) => {
      if (e.key === "Escape") animateTo(computedMaxHeight);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, animateTo, computedMaxHeight]);

  // If closed (open=false), keep it offscreen with no rendering interactivity
  if (!open) {
    return null;
  }

  const sheetStyle: React.CSSProperties = {
    transform: `translateY(${translateY}px)`,
    transition: animRef.current
      ? "none"
      : "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
    touchAction: "none",
    willChange: "transform",
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    height: computedMaxHeight,
    maxHeight: computedMaxHeight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  return (
    <div
      ref={backdropRef}
      onMouseDown={onBackdropClick}
      onTouchStart={onBackdropClick}
      className="bottom-sheet-overlay"
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
    >
      <div className="bottom-sheet" ref={sheetRef} style={sheetStyle}>
        {/* Scrollable content area */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            padding: 16,
          }}
          // Prevent scroll from hijacking drag when at bounds
          onWheel={(e) => {
            const el = e.currentTarget;
            const atTop = el.scrollTop <= 0;
            const atBottom =
              el.scrollHeight - el.scrollTop - el.clientHeight <= 0;
            if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
              // Let drag take over if user tries to scroll beyond bounds
              // Do nothing special here; pointer handlers handle drag
            }
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
